import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import DocumentInfoTab from "./DocumentInfoTab";
import PurchaseOrderTab from "./PurchaseOrderTab";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import BreadcrumbsLayout from "../../../components/UI/BreadcrumbsLayout";
import { ActivityLogSlideInPanel } from "../../../components/UI/SlideInPanel/ActivityLogSlideInPanel";
import {
  purchaseRequestSchema,
  purchaseRequestValidation,
} from "../../../components/Form/Purchase/Request/schema";

import { IBreadcrumbsAndMenu, ITab } from "../../../types/global";
import { ActivityLogDocumentType } from "../../../generated/inventory";
import { IPurchaseRequest } from "../../../types/Purchase/purchaseRequest";
import {
  PurchaseRequestFindUniqueQuery,
  usePurchaseRequestFindUniqueQuery,
} from "../../../generated/purchase";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useStateContext } from "../../../contexts/auth-context";

const PurchaseRequestContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const graphQLClient = createGraphQLClientWithMiddleware("purchase");
  const navigate = useNavigate();
  const {
    state: { authUser, permissions },
  } = useStateContext();

  const methods = useForm<IPurchaseRequest>({
    defaultValues: { ...purchaseRequestSchema },
    resolver: yupResolver<any>(purchaseRequestValidation),
  });

  const { control } = methods;

  const { data, isLoading, isSuccess } =
    usePurchaseRequestFindUniqueQuery<PurchaseRequestFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    if (isSuccess && permissions?.purchase_request?.view_self) {
      const createdBy = data?.PurchaseRequestFindUnique?.created_by;
      const relatedUserList =
        data?.PurchaseRequestFindUnique?.related_user_list;
      if (
        createdBy?.user_unique_id === authUser?.unique_id ||
        relatedUserList?.find(
          (user) => user.user_unique_id === authUser?.unique_id
        )
      ) {
        return;
      } else {
        navigate(`/unauthorized`, {
          replace: true,
        });
      }
    }
  }, [
    authUser?.unique_id,
    data?.PurchaseRequestFindUnique?.created_by,
    data?.PurchaseRequestFindUnique?.related_user_list,
    isSuccess,
    navigate,
    permissions?.purchase_request?.view_self,
  ]);

  const tabs: ITab[] = [
    {
      label: t("purchase.document_info"),
      path: `${pathname}`,
    },
    {
      label: t("purchase.order.index"),
      path: `${pathname}?tab=purchase_order`,
    },
  ];

  const renderTab = (tab: string | null) => {
    switch (tab) {
      case "purchase_order":
        return (
          <PurchaseOrderTab
            referenceUniqueId={data?.PurchaseRequestFindUnique.unique_id}
            isLoading={isLoading}
          />
        );
      default:
        return <DocumentInfoTab />;
    }
  };

  const currentTab = pathname + (tab ? `?tab=${tab}` : "");

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("purchase.index"),
      to: "/purchase",
    },
    {
      name: t("purchase.request.index"),
      to: "/purchase/request",
    },
    {
      name: id
        ? data?.PurchaseRequestFindUnique.unique_id || "-"
        : t("button.create") + t("purchase.request.index"),
    },
  ];

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  return (
    <FormProvider {...methods}>
      <BreadcrumbsLayout
        breadcrumbs={breadcrumbs}
        setOpenDrawer={setOpenDrawer}
      />
      {(tab === "purchase_order" ||
        status === "approved" ||
        status === "partially_ordered" ||
        status === "fully_ordered") && (
        <CustomizedTab tabs={tabs} currentTab={currentTab} />
      )}
      {renderTab(tab)}
      {id && (
        <ActivityLogSlideInPanel
          open={openDrawer}
          handleClose={() => setOpenDrawer(false)}
          documentId={id!}
          documentType={ActivityLogDocumentType.PurchaseRequest}
        />
      )}
    </FormProvider>
  );
};

export default PurchaseRequestContainer;
